import React from 'react';
import { FormSelect } from 'components/form';
import { Edit, Refresh } from '@mui/icons-material';
import { EstimatedCloseDatePicker } from 'modules/vendorOpportunity';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { ClosedDealInfo } from '../ClosedDealInfo';
import { ActionsProps, LoadingProps } from './types';
import { ClosedDealData } from '../../api/dealLifeCycle';
import { Nullable, SelectOption } from 'global';
import { Stage, DepartmentId } from '../../api';

interface DealActionsProps {
  voId: string;
  isDealClosed: boolean;
  closeInfo?: ClosedDealData;
  activeStage: Nullable<Stage>;
  loading: LoadingProps['loading'];
  estimatedCloseDate?: string | null;
  departmentsOptions: SelectOption[];
  hasActiveStageUnfilledRequired: boolean;
  activeDepartmentId: Nullable<DepartmentId>;
  actions: Pick<
    ActionsProps['actions'],
    'onMarkAsWon' | 'onMarkAsLost' | 'onReopenDeal' | 'onChangeDepartment' | 'onUpdateCloseDeal'
  >;
}

export const DealActions = ({
  voId,
  loading,
  actions,
  closeInfo,
  activeStage,
  isDealClosed,
  estimatedCloseDate,
  activeDepartmentId,
  departmentsOptions,
  hasActiveStageUnfilledRequired,
}: DealActionsProps) => {
  return (
    <Box className="firstSection">
      <Stack direction="row" gap="0.5rem" alignItems="center">
        <Box sx={{ width: '12rem' }}>
          <EstimatedCloseDatePicker voId={voId} estimatedCloseDate={estimatedCloseDate} />
        </Box>

        <RenderWithCondition condition={!isDealClosed && (activeStage?.meta?.isLast ?? false)}>
          <Tooltip title={hasActiveStageUnfilledRequired ? 'Complete Required Attributes before Complete Stage' : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                onClick={actions.onMarkAsWon}
                disabled={hasActiveStageUnfilledRequired}
              >
                Mark as Won
              </Button>
            </span>
          </Tooltip>
        </RenderWithCondition>

        <RenderWithCondition condition={!isDealClosed}>
          <Button size="small" onClick={actions.onMarkAsLost}>
            Close Deal
          </Button>
        </RenderWithCondition>

        <RenderWithCondition condition={!!closeInfo}>
          <Button onClick={actions.onReopenDeal} startIcon={<Refresh />} loading={loading.isDealReopening}>
            Re-open
          </Button>

          <ClosedDealInfo
            {...closeInfo!}
            editNode={
              <Tooltip title="Edit Close Deal">
                <IconButton size="small" onClick={actions.onUpdateCloseDeal} disabled={loading.isCloseDealUpdating}>
                  <Edit fontSize="small" color={closeInfo?.closeDealStatus === 'SUCCESS' ? 'success' : 'error'} />
                </IconButton>
              </Tooltip>
            }
          />
        </RenderWithCondition>
      </Stack>

      <Box sx={{ width: '12rem' }}>
        <FormSelect
          variant="outlined"
          label="Department"
          value={activeDepartmentId}
          options={departmentsOptions}
          onValueChange={actions.onChangeDepartment}
        />
      </Box>
    </Box>
  );
};
